import React from 'react';
import {useTranslation} from 'react-i18next';
import {resolveCssColor} from '@chekinapp/ui';
import {loadStripe} from '@stripe/stripe-js/pure';
import type {StripeElementLocale, StripeElementsOptions} from '@stripe/stripe-js';
import {usePaymentSettings} from './hooks/usePaymentSettings';

export type StripeInstanceOptions = {
  publishableKey: string;
};

export function useStripeElementsOptions(): StripeElementsOptions {
  const {i18n} = useTranslation();
  const {currency} = usePaymentSettings();

  return React.useMemo(() => {
    const resolveColor = (value: string) => resolveCssColor(value, document);

    return {
      locale: i18n.language as StripeElementLocale,
      mode: 'setup' as const,
      currency: currency?.toLowerCase() || 'eur',
      appearance: {
        variables: {
          colorPrimary: resolveColor('var(--primary)'),
          colorBackground: resolveColor('var(--card-bg)'),
          colorText: resolveColor('var(--font-primary)'),
          fontFamily: 'system-ui, sans-serif',
          borderRadius: '8px',
        },
      },
      loader: 'never' as const,
    };
  }, [i18n.language, currency]);
}

export function useStripeInstance({publishableKey}: StripeInstanceOptions) {
  const {paymentSettings, isStripePaymentProvider} = usePaymentSettings();
  const stripeAccount = paymentSettings?.external_account_id;
  const paymentSettingsMode = paymentSettings?.mode;

  const stripe = React.useMemo(() => {
    const hasICPlusPayment = paymentSettingsMode === 'IC_PLUS';
    const needLoadStripe =
      isStripePaymentProvider && (hasICPlusPayment || !!stripeAccount);
    const actualStripeAccount = hasICPlusPayment ? undefined : stripeAccount;

    if (!needLoadStripe) {
      return null;
    }

    return loadStripe(publishableKey, {stripeAccount: actualStripeAccount});
  }, [isStripePaymentProvider, stripeAccount, paymentSettingsMode, publishableKey]);

  return {stripe, stripeAccount};
}
